import axios from "axios";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link } from "react-router-dom";

import Badge from "@mui/material/Badge";
import MailIcon from "@mui/icons-material/Mail";
import AlarmIcon from "@mui/icons-material/Alarm";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useAuth } from "../context/AuthContext";
import { baseUrl, userName } from "./Univ.js";

const LOW_STOCK_LIMIT = 20;
const EXPIRY_DAYS_LIMIT = 20;
const PHARMACY_REFRESH_MS = 60000;
const SYSTEM_ALERT_REFRESH_MS = 60000;

// ---------------------------------------------------------
// API HELPERS
// ---------------------------------------------------------

const getApiBaseUrl = () => baseUrl.replace(/\/+$/, "");

const getTenantHeaders = () => {
    const hospitalCode =
        localStorage.getItem("hospital_code") ||
        localStorage.getItem("hospitalCode") ||
        "";

    return hospitalCode
        ? { "X-Hospital-Code": hospitalCode }
        : {};
};

const apiGet = (path, config = {}) =>
    axios.get(
        `${getApiBaseUrl()}/${path.replace(/^\/+/, "")}`,
        {
            ...config,
            headers: {
                ...getTenantHeaders(),
                ...(config.headers || {}),
            },
        }
    );

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
};

// ---------------------------------------------------------
// DATE HELPERS
// ---------------------------------------------------------

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";

    const date = new Date(dateTimeString);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
};

// Parse date-only values without timezone date shifts.
const parseExpiryDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        );
    }

    const text = String(value).trim();

    if (
        !text ||
        text === "-" ||
        text.toLowerCase() === "null"
    ) {
        return null;
    }

    const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoMatch) {
        const date = new Date(
            Number(isoMatch[1]),
            Number(isoMatch[2]) - 1,
            Number(isoMatch[3])
        );

        return Number.isNaN(date.getTime()) ? null : date;
    }

    const dmyMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (dmyMatch) {
        const date = new Date(
            Number(dmyMatch[3]),
            Number(dmyMatch[2]) - 1,
            Number(dmyMatch[1])
        );

        return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(text);

    if (Number.isNaN(parsed.getTime())) return null;

    return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
    );
};

// ---------------------------------------------------------
// PHARMACY HELPERS
// ---------------------------------------------------------

const getStockQuantity = (item) => {
    const value =
        item?.qty ??
        item?.totqty ??
        item?.quantity ??
        item?.balance_qty ??
        item?.stock_qty ??
        item?.product?.qty ??
        item?.product?.totqty ??
        0;

    const quantity = Number(value);

    return Number.isFinite(quantity) ? quantity : 0;
};

const getStockExpiry = (item) =>
    item?.expiryDateObj ??
    item?.expiry ??
    item?.expiry_date ??
    item?.expiryDate ??
    item?.exp_date ??
    item?.batch_expiry ??
    item?.product?.expiry ??
    item?.product?.expiry_date ??
    item?.product?.expiryDate ??
    item?.product?.exp_date ??
    null;

const getStockKey = (item, index) => {
    const id =
        item?.id ??
        item?.product_id ??
        item?.product?.id;

    const batch = item?.batch ?? item?.batch_no ?? "";

    if (id != null) {
        return `stock-${id}-${batch}`;
    }

    return [
        item?.product?.product ??
        item?.product ??
        item?.product_name ??
        "item",
        batch,
        getStockExpiry(item) ?? "",
        index,
    ].join("-");
};

// ---------------------------------------------------------
// DATABASE MENU HELPERS
// ---------------------------------------------------------

const normalizeRoute = (value) => {
    if (!value) return "";

    return (
        String(value)
            .trim()
            .replace(/\/+$/, "")
            .toLowerCase() || "/"
    );
};

const getItemRoute = (item) =>
    item?.link ||
    item?.route ||
    "";

const isDashboardItem = (item) => {
    const title = String(item?.title || "")
        .trim()
        .toLowerCase();

    const category = String(item?.category || "")
        .trim()
        .toLowerCase();

    const route = normalizeRoute(getItemRoute(item));

    return (
        title === "dashboard" ||
        title === "home" ||
        category === "dashboard" ||
        category === "home" ||
        route === "/"
    );
};

const canAccessPharmacyAlerts = (items) =>
    items.some((item) => {
        const title = String(item?.title || "").toLowerCase();
        const category = String(item?.category || "").toLowerCase();
        const route = normalizeRoute(getItemRoute(item));

        return (
            category.includes("pharmacy") ||
            category.includes("store") ||
            title.includes("pharmacy") ||
            route.includes("pharm-menu") ||
            route.includes("pharm-store") ||
            route.includes("indent-expiry-list")
        );
    });

// ---------------------------------------------------------
// USER IDENTITY HELPERS
// ---------------------------------------------------------

const getCurrentUsername = () => {
    return (
        localStorage.getItem("username") ||
        localStorage.getItem("user_login") ||
        userName() ||
        ""
    ).trim();
};

const getCurrentLoginStatus = (authContext) => {
    const {
        isAuthenticated,
        authTokens,
        username,
    } = authContext;

    // AuthContext is authoritative when it explicitly provides
    // a boolean authentication state.
    if (typeof isAuthenticated === "boolean") {
        return isAuthenticated;
    }

    if (authTokens?.access) {
        return true;
    }

    if (localStorage.getItem("authTokens")) {
        try {
            const tokens = JSON.parse(
                localStorage.getItem("authTokens")
            );

            if (tokens?.access) return true;
        } catch {
            // Fall through to legacy login state.
        }
    }

    const storedLogin = localStorage.getItem("user_login");

    return Boolean(
        username ||
        getCurrentUsername() ||
        (
            storedLogin &&
            storedLogin !== "false" &&
            storedLogin !== "null" &&
            storedLogin !== "undefined"
        )
    );
};

// =========================================================
// HEADER COMPONENT
// =========================================================

function Header() {
    const audioRef = useRef(null);
    const prevAlertCount = useRef(0);

    const [searchString, setSearchString] = useState("");
    const [alerts, setAlerts] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [activities, setActivities] = useState([]);

    const [menuItems, setMenuItems] = useState([]);
    const [menuLoading, setMenuLoading] = useState(false);
    const [menuError, setMenuError] = useState("");

    const [pharmacyAlertCount, setPharmacyAlertCount] = useState(0);
    const [pharmacyExpiryCount, setPharmacyExpiryCount] = useState(0);
    const [pharmacyLowStockCount, setPharmacyLowStockCount] = useState(0);

    const auth = useAuth();

    const {
        logoutUser,
        username: contextUsername,
    } = auth;

    // Always prefer the current AuthContext username.
    // Use localStorage as a fallback for legacy login code.
    const username = String(
        contextUsername ||
        getCurrentUsername() ||
        ""
    ).trim();

    const isLoggedIn = getCurrentLoginStatus(auth);

    // -----------------------------------------------------
    // DATABASE-DRIVEN MENU
    // -----------------------------------------------------

    const fetchMenu = useCallback(async () => {
        if (!isLoggedIn) {
            setMenuItems([]);
            setMenuError("");
            setMenuLoading(false);
            return;
        }

        setMenuLoading(true);
        setMenuError("");

        try {
            const response = await apiGet("menu/");
            const items = getArray(response.data);

            if (!Array.isArray(items)) {
                throw new Error(
                    "The menu API returned an invalid response."
                );
            }

            const normalizedItems = items
                .filter((item) => item?.enabled !== false)
                .filter((item) => getItemRoute(item))
                .map((item) => ({
                    ...item,
                    link: getItemRoute(item),
                    title: item?.title || "Untitled menu",
                }));

            setMenuItems(normalizedItems);
        } catch (error) {
            console.error(
                "Unable to load DrHiS menu:",
                error.response?.data || error.message
            );

            setMenuItems([]);

            setMenuError(
                error.response?.data?.detail ||
                "Unable to load menu permissions."
            );
        } finally {
            setMenuLoading(false);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        fetchMenu();
    }, [fetchMenu]);

    const dashboardItem = useMemo(
        () => menuItems.find(isDashboardItem),
        [menuItems]
    );

    const homeLink = useMemo(() => {
        const configuredHome = menuItems.find(
            (item) =>
                item.home_link ||
                item.is_home === true
        );

        return (
            configuredHome?.home_link ||
            configuredHome?.link ||
            dashboardItem?.link ||
            menuItems[0]?.link ||
            "/"
        );
    }, [menuItems, dashboardItem]);

    const brandLink = useMemo(() => {
        const configuredMenu = menuItems.find(
            (item) =>
                item.menu_link ||
                item.is_menu_home === true
        );

        return (
            configuredMenu?.menu_link ||
            menuItems.find((item) => item.menu_link)?.menu_link ||
            "/admin-menu"
        );
    }, [menuItems]);

    const canViewPharmacyAlerts = useMemo(
        () => canAccessPharmacyAlerts(menuItems),
        [menuItems]
    );

    // -----------------------------------------------------
    // ACTIVITIES
    // -----------------------------------------------------

    const fetchActivities = useCallback(async () => {
        try {
            const response = await apiGet("activities/");
            setActivities(getArray(response.data));
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error(
                    "Error fetching activities:",
                    error.response?.data || error.message
                );
            }
        }
    }, []);

    // -----------------------------------------------------
    // TASKS
    // -----------------------------------------------------

    const fetchTasks = useCallback(async () => {
        try {
            const response = await apiGet("get-todo/");
            setTasks(getArray(response.data));
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error(
                    "Error fetching tasks:",
                    error.response?.data || error.message
                );
            }
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setActivities([]);
            setTasks([]);
            return;
        }

        fetchActivities();
        fetchTasks();
    }, [isLoggedIn, fetchActivities, fetchTasks]);

    // -----------------------------------------------------
    // SYSTEM ALERTS
    // -----------------------------------------------------

    const playAlertSound = useCallback(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio("/alert.mp3");
            audioRef.current.loop = true;
        }

        audioRef.current.play().catch(() => {
            // Browser audio may require user interaction.
        });
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            setAlerts([]);
            prevAlertCount.current = 0;

            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            return;
        }

        let cancelled = false;

        const fetchSystemAlerts = async () => {
            try {
                const response = await apiGet("system-alerts/");

                if (cancelled) return;

                const data = getArray(response.data);

                if (data.length > prevAlertCount.current) {
                    playAlertSound();
                }

                prevAlertCount.current = data.length;
                setAlerts(data);
            } catch (error) {
                if (
                    error.response?.status !== 401 &&
                    !cancelled
                ) {
                    console.error(
                        "Error fetching system alerts:",
                        error.response?.data || error.message
                    );
                }
            }
        };

        fetchSystemAlerts();

        const interval = setInterval(
            fetchSystemAlerts,
            SYSTEM_ALERT_REFRESH_MS
        );

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [isLoggedIn, playAlertSound]);

    // -----------------------------------------------------
    // PHARMACY LOW-STOCK AND EXPIRY ALERTS
    // -----------------------------------------------------

    const fetchPharmacyAlerts = useCallback(async () => {
        if (!isLoggedIn || !canViewPharmacyAlerts) {
            setPharmacyAlertCount(0);
            setPharmacyExpiryCount(0);
            setPharmacyLowStockCount(0);
            return;
        }

        try {
            const response = await apiGet(
                "indent-pharm-search/?qty=gt0"
            );

            const stockRows = getArray(response.data);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const expiryLimit = new Date(today);
            expiryLimit.setDate(
                expiryLimit.getDate() + EXPIRY_DAYS_LIMIT
            );

            const expiringKeys = new Set();
            const lowStockKeys = new Set();
            const allAlertKeys = new Set();

            stockRows.forEach((item, index) => {
                const key = getStockKey(item, index);
                const quantity = getStockQuantity(item);
                const expiryDate = parseExpiryDate(
                    getStockExpiry(item)
                );

                if (quantity < LOW_STOCK_LIMIT) {
                    lowStockKeys.add(key);
                    allAlertKeys.add(key);
                }

                if (
                    expiryDate &&
                    expiryDate <= expiryLimit
                ) {
                    expiringKeys.add(key);
                    allAlertKeys.add(key);
                }
            });

            setPharmacyExpiryCount(expiringKeys.size);
            setPharmacyLowStockCount(lowStockKeys.size);
            setPharmacyAlertCount(allAlertKeys.size);
        } catch (error) {
            if (error.response?.status !== 401) {
                console.error(
                    "Error fetching pharmacy alerts:",
                    error.response?.data || error.message
                );
            }
        }
    }, [isLoggedIn, canViewPharmacyAlerts]);

    useEffect(() => {
        if (!isLoggedIn || !canViewPharmacyAlerts) {
            setPharmacyAlertCount(0);
            setPharmacyExpiryCount(0);
            setPharmacyLowStockCount(0);
            return;
        }

        fetchPharmacyAlerts();

        const interval = setInterval(
            fetchPharmacyAlerts,
            PHARMACY_REFRESH_MS
        );

        return () => clearInterval(interval);
    }, [
        isLoggedIn,
        canViewPharmacyAlerts,
        fetchPharmacyAlerts,
    ]);

    // -----------------------------------------------------
    // TODAY'S REMINDERS
    // -----------------------------------------------------

    const reminderTasks = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (!username) return [];

        return activities.filter((row) => {
            if (
                !row?.reminder_date ||
                row?.addedby !== username
            ) {
                return false;
            }

            const reminderDate = parseExpiryDate(
                row.reminder_date
            );

            return (
                reminderDate &&
                reminderDate.getTime() === today.getTime()
            );
        });
    }, [activities, username]);

    // -----------------------------------------------------
    // UNREAD TASKS
    // -----------------------------------------------------

    const unreadTasks = useMemo(() => {
        if (!username) return [];

        return tasks.filter(
            (row) =>
                Number(row?.status) === 1 &&
                row?.addedby === username
        );
    }, [tasks, username]);

    const countReminders = reminderTasks.length;
    const countUnreadTasks = unreadTasks.length;

    const reminderBadgeColor =
        countReminders > 0 ? "warning" : "default";

    const reminderIconColor =
        countReminders > 0 ? "error" : "action";

    // -----------------------------------------------------
    // EMR SEARCH
    // -----------------------------------------------------

    const handleChange = (event) => {
        setSearchString(event.target.value);
    };

    const searchCourse = (event) => {
        event.preventDefault();

        const search = searchString.trim();

        if (search) {
            window.location.href =
                `/emr-details/${encodeURIComponent(search)}`;
        }
    };

    // -----------------------------------------------------
    // LOGOUT
    // -----------------------------------------------------

    const handleLogout = async () => {
        setAlerts([]);
        setActivities([]);
        setTasks([]);
        setMenuItems([]);
        prevAlertCount.current = 0;

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }

        try {
            await Promise.resolve(logoutUser());
        } finally {
            window.location.href = "/user-login/";
        }
    };

    // -----------------------------------------------------
    // RENDER
    // -----------------------------------------------------

    return (
        <nav className="navbar navbar-expand-lg navbar-dark custom-navbar">
            <div className="container">
                <button
                    className="navbar-toggler"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#navbarTogglerDemo01"
                    aria-controls="navbarTogglerDemo01"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon" />
                </button>

                <div
                    className="collapse navbar-collapse"
                    id="navbarTogglerDemo01"
                >
                    {isLoggedIn && (
                        <>
                            {/* BRAND */}
                            <Link
                                className="navbar-brand"
                                to={brandLink}
                                title={
                                    menuLoading
                                        ? "Loading menu..."
                                        : menuError || "MGM DrHiS"
                                }
                            >
                                MGM DrHiS
                            </Link>

                            {/* PATIENT EMR SEARCH */}
                            <form
                                className="d-none d-md-flex"
                                onSubmit={searchCourse}
                            >
                                <input
                                    name="search"
                                    value={searchString}
                                    onChange={handleChange}
                                    className="form-control me-2"
                                    type="search"
                                    placeholder="Search by Patient ID for EMR"
                                    aria-label="Search patient EMR"
                                />

                                <button
                                    className="btn btn-info"
                                    type="submit"
                                >
                                    Search
                                </button>
                            </form>

                            <ul className="navbar-nav ms-auto flex-row align-items-center gap-3">
                                {/* HOME */}
                                <li className="nav-item">
                                    <Tooltip title="Home">
                                        <Link
                                            className="navbar-brand"
                                            to={homeLink}
                                            aria-label="Home"
                                        >
                                            <FontAwesomeIcon
                                                icon="fa-solid fa-house-user"
                                                size="1x"
                                                color="#ffc107"
                                            />
                                        </Link>
                                    </Tooltip>
                                </li>

                                {/* PHARMACY ALERTS */}
                                {canViewPharmacyAlerts && (
                                    <li className="nav-item">
                                        <Tooltip
                                            title={
                                                `Pharmacy alerts: ${pharmacyAlertCount} total | ` +
                                                `${pharmacyExpiryCount} expiry | ` +
                                                `${pharmacyLowStockCount} low stock`
                                            }
                                        >
                                            <span>
                                                <Badge
                                                    badgeContent={pharmacyAlertCount}
                                                    color={
                                                        pharmacyAlertCount > 0
                                                            ? "error"
                                                            : "default"
                                                    }
                                                    max={999}
                                                >
                                                    <Link
                                                        to="/indent-expiry-list/"
                                                        aria-label="Pharmacy stock alerts"
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            color:
                                                                pharmacyAlertCount > 0
                                                                    ? "#dc2626"
                                                                    : "#166534",
                                                        }}
                                                    >
                                                        <LocalPharmacyIcon
                                                            sx={{
                                                                fontSize: 27,
                                                                color:
                                                                    pharmacyAlertCount > 0
                                                                        ? "#dc2626"
                                                                        : "#16a34a",
                                                            }}
                                                        />
                                                    </Link>
                                                </Badge>
                                            </span>
                                        </Tooltip>
                                    </li>
                                )}

                                {/* TODO */}
                                <li className="nav-item">
                                    <Tooltip title="Todo list">
                                        <Badge
                                            color={
                                                countUnreadTasks > 0
                                                    ? "warning"
                                                    : "default"
                                            }
                                            badgeContent={countUnreadTasks}
                                        >
                                            <Link
                                                to="/todolist"
                                                aria-label="Todo list"
                                            >
                                                <MailIcon
                                                    color={
                                                        countUnreadTasks > 0
                                                            ? "error"
                                                            : "action"
                                                    }
                                                />
                                            </Link>
                                        </Badge>
                                    </Tooltip>
                                </li>

                                {/* SYSTEM ALERTS */}
                                <li className="nav-item">
                                    <Tooltip title="System alerts">
                                        <Badge
                                            color={
                                                alerts.length > 0
                                                    ? "error"
                                                    : "default"
                                            }
                                            badgeContent={alerts.length}
                                        >
                                            <Link
                                                to="/alerts"
                                                aria-label="System alerts"
                                            >
                                                <NotificationsActiveIcon
                                                    color={
                                                        alerts.length > 0
                                                            ? "error"
                                                            : "action"
                                                    }
                                                />
                                            </Link>
                                        </Badge>
                                    </Tooltip>
                                </li>

                                {/* REMINDERS */}
                                <li className="nav-item">
                                    <Tooltip title="Today's reminders">
                                        <Badge
                                            color={reminderBadgeColor}
                                            badgeContent={countReminders}
                                        >
                                            <Link
                                                to="/lead-list"
                                                aria-label="Reminders"
                                            >
                                                <AlarmIcon
                                                    color={reminderIconColor}
                                                />
                                            </Link>
                                        </Badge>
                                    </Tooltip>
                                </li>

                                {/* USER DROPDOWN */}
                                <li className="nav-item dropdown">
                                    <button
                                        className="nav-link dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{
                                            background: "transparent",
                                            border: 0,
                                            color: "inherit",
                                        }}
                                    >
                                        {username || "User"}
                                    </button>

                                    <ul className="dropdown-menu dropdown-menu-end">
                                        <li>
                                            <span className="dropdown-item-text">
                                                Welcome: {username || "User"}
                                            </span>
                                        </li>

                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to={brandLink}
                                            >
                                                Menu
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                className="dropdown-item"
                                                to="/change-password"
                                            >
                                                Change Password
                                            </Link>
                                        </li>

                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={handleLogout}
                                            >
                                                Logout
                                            </button>
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
}

export { formatDateTime };
export default Header;
